import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../Api';

const fmtN = (n) => Number(n || 0).toLocaleString('fr-FR');

const Bar = ({ pct, gradient }) => (
  <div style={{ width: '100%', background: '#f0f0f0', borderRadius: 999, height: 6, overflow: 'hidden' }}>
    <div style={{ height: 6, width: `${pct}%`, background: gradient, transition: 'width 0.7s ease' }} />
  </div>
);

const Row = ({ label, value, pct, gradient, children }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', marginBottom: 4 }}>
      <span style={{ color: '#9e9e9e' }}>{label}</span>
      <span style={{ color: '#424242', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
    {pct !== null && pct !== undefined && <Bar pct={pct} gradient={gradient} />}
    {children}
  </div>
);

const SurveyProgressBars = ({ surveyId, surveyWaveId }) => {
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!surveyId) return;
    setLoading(true);
    // Tracker slot (has a vague) → wave-scoped progress; otherwise the survey-wide endpoint.
    const req = surveyWaveId
      ? api.get(`/cati/surveys/${surveyId}/wave-progress`, { params: { survey_wave_id: surveyWaveId } })
          .then(res => res.data?.data || null)
      : api.get('/cati/surveys/progress', { params: { survey_id: surveyId } })
          .then(res => {
            const payload = res.data?.data || {};
            const cati = Array.isArray(payload.cati) ? payload.cati : [];
            const f2f = Array.isArray(payload.f2f) ? payload.f2f : [];
            return [...cati, ...f2f].find(s => Number(s.id) === Number(surveyId)) || null;
          });
    req.then(setSurvey).catch(() => setSurvey(null)).finally(() => setLoading(false));
  }, [surveyId, surveyWaveId]);

  if (!surveyId) return null;
  if (loading) {
    return (
      <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
        {['Complétions', 'Contrôle', 'Budget'].map(label => (
          <div key={label} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', marginBottom: 4 }}>
              <span style={{ color: '#9e9e9e' }}>{label}</span>
              <span style={{ color: '#d4d4d4' }}>Chargement…</span>
            </div>
            <div className="progress-skeleton" style={{ width: '100%', background: '#ececec', borderRadius: 999, height: 6, overflow: 'hidden' }} />
          </div>
        ))}
        <style>{`
          @keyframes progressSkeletonShimmer {
            0% { background-position: -150px 0; }
            100% { background-position: 150px 0; }
          }
          .progress-skeleton {
            background: linear-gradient(90deg, #ececec 0%, #f5f5f5 50%, #ececec 100%) !important;
            background-size: 200px 100% !important;
            animation: progressSkeletonShimmer 1.2s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }
  if (!survey) return null;

  const target = Number(survey.general_target || 0);
  const completionPct = target > 0 ? Math.min(100, Math.round((survey.completions / target) * 100)) : null;
  const completionGrad = completionPct === null ? '' : completionPct >= 80 ? 'linear-gradient(90deg,#22c55e,#16a34a)' : completionPct >= 50 ? 'linear-gradient(90deg,#facc15,#eab308)' : 'linear-gradient(90deg,#f87171,#dc2626)';

  const controlPct = target > 0 ? Math.min(100, Math.round((survey.controls_done / target) * 100)) : null;

  const budgetPct = survey.has_budget && survey.total_budget > 0
    ? Math.min(100, Math.round((survey.budget_spent / survey.total_budget) * 1000) / 10)
    : null;
  const budgetGrad = budgetPct === null ? '' : budgetPct >= 90 ? 'linear-gradient(90deg,#f87171,#dc2626)' : budgetPct >= 70 ? 'linear-gradient(90deg,#facc15,#eab308)' : 'linear-gradient(90deg,#22c55e,#16a34a)';

  return (
    <div
      onClick={() => navigate(`/cati/surveys/${surveyId}/stats`)}
      style={{ cursor: 'pointer', background: '#fafafa', borderRadius: 8, padding: '12px 14px', marginBottom: 10, transition: 'background 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
      onMouseLeave={e => e.currentTarget.style.background = '#fafafa'}
      title="Voir les statistiques"
    >
      <Row
        label="Complétions"
        value={<>
          {fmtN(survey.completions)}
          {target > 0 && <span style={{ color: '#bdbdbd', fontWeight: 400 }}> / {fmtN(target)} ({completionPct}%)</span>}
          {/* Frozen count taken from the quota cells: the productivity job stops writing
              rows once the quota is finished, so this number no longer moves. */}
          {survey.completions_frozen && (
            <span style={{ color: '#9e9e9e', fontWeight: 400 }}> (quota terminé)</span>
          )}
        </>}
        pct={completionPct}
        gradient={completionGrad}
      />

      <Row
        label="Contrôle"
        value={<>
          {fmtN(survey.controls_done)}
          {target > 0 && <span style={{ color: '#bdbdbd', fontWeight: 400 }}> / {fmtN(target)} ({controlPct}%)</span>}
        </>}
        pct={controlPct}
        gradient="linear-gradient(90deg,#60a5fa,#3b82f6)"
      />

      {survey.has_budget ? (
        <Row
          label="Budget"
          value={<>
            {fmtN(survey.budget_spent)}
            <span style={{ color: '#bdbdbd', fontWeight: 400 }}> / {fmtN(survey.total_budget)} MAD{budgetPct !== null && <> ({budgetPct}%)</>}</span>
          </>}
          pct={budgetPct}
          gradient={budgetGrad}
        />
      ) : (
        <div style={{ fontSize: '0.7rem', color: '#bdbdbd', marginTop: 4 }}>Budget non défini</div>
      )}
    </div>
  );
};

export default SurveyProgressBars;
